import { useEffect, useState } from 'react';
import { KeyRound, Plus, Trash2, AlertCircle, Power, PowerOff, ShieldCheck } from 'lucide-react';
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, Input } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { idpApi, ApiError } from '@solara/api-client';
import type { IdpConnectionDto, IdpType } from '@solara/api-client';

const IDP_TYPE_KEYS: readonly IdpType[] = [
  'MICROSOFT_365',
  'GOOGLE',
  'OKTA',
  'AZURE_AD',
  'SAML2_GENERIC',
  'OIDC_GENERIC',
];

interface DraftIdp {
  id?: string;
  idpType: IdpType;
  displayName: string;
  metadataUrl: string;
  clientId: string;
  clientSecret: string;
  attributeMappingJson: string;
  isActive: boolean;
}

const EMPTY_DRAFT: DraftIdp = {
  idpType: 'MICROSOFT_365',
  displayName: 'Microsoft 365 SSO',
  metadataUrl: '',
  clientId: '',
  clientSecret: '',
  attributeMappingJson: '{\n  "email": "preferred_username",\n  "name": "name"\n}',
  isActive: false,
};

export default function IdpConnectionsPage() {
  const { t } = useTranslation('p1');
  const [connections, setConnections] = useState<IdpConnectionDto[]>([]);
  const [types, setTypes] = useState<IdpType[]>([]);
  const [draft, setDraft] = useState<DraftIdp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const typeLabel = (idp: IdpType) => t(`idp.types.${idp}`, { defaultValue: idp });

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    try {
      const [list, supported] = await Promise.all([idpApi.list(), idpApi.types()]);
      setConnections(list);
      setTypes(supported);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    }
  }

  function startEdit(conn: IdpConnectionDto) {
    setDraft({
      id: conn.id,
      idpType: conn.idpType,
      displayName: conn.displayName,
      metadataUrl: conn.metadataUrl ?? '',
      clientId: conn.clientId ?? '',
      clientSecret: '',
      attributeMappingJson: JSON.stringify(conn.attributeMapping ?? {}, null, 2),
      isActive: conn.isActive,
    });
  }

  function parseMapping(json: string): Record<string, unknown> | null {
    if (!json.trim()) return {};
    try {
      const parsed = JSON.parse(json);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
      return parsed;
    } catch {
      return null;
    }
  }

  async function save() {
    if (!draft) return;
    const mapping = parseMapping(draft.attributeMappingJson);
    if (mapping === null) {
      setError(t('idp.mappingError'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (draft.id) {
        await idpApi.update(draft.id, {
          displayName: draft.displayName,
          metadataUrl: draft.metadataUrl || undefined,
          clientId: draft.clientId || undefined,
          clientSecret: draft.clientSecret || undefined,
          attributeMapping: mapping,
          isActive: draft.isActive,
        });
      } else {
        await idpApi.upsert({
          idpType: draft.idpType,
          displayName: draft.displayName,
          metadataUrl: draft.metadataUrl || undefined,
          clientId: draft.clientId || undefined,
          clientSecret: draft.clientSecret || undefined,
          attributeMapping: mapping,
          isActive: draft.isActive,
        });
      }
      setDraft(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(conn: IdpConnectionDto) {
    setBusy(true);
    try {
      await idpApi.setActive(conn.id, !conn.isActive);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function remove(conn: IdpConnectionDto) {
    if (!window.confirm(t('idp.confirmDelete', { name: conn.displayName }))) return;
    setBusy(true);
    try {
      await idpApi.remove(conn.id);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.deleteFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6 max-w-5xl">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('idp.title')}</h1>
          <p className="text-fg-muted text-sm">{t('idp.subtitle')}</p>
        </div>
        <Button onClick={() => setDraft({ ...EMPTY_DRAFT })}>
          <Plus className="mr-2 h-4 w-4" />
          {t('idp.newConnection')}
        </Button>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      {draft && (
        <Card className="rounded-2xl border-brand-500/30">
          <CardHeader>
            <CardTitle>{draft.id ? t('idp.editConnection') : t('idp.newConnectionTitle')}</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3 text-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="block text-sm">
                <span className="text-xs text-fg-muted">{t('idp.idpType')}</span>
                <select
                  className="w-full mt-1 rounded border border-border bg-surface px-3 py-2 text-sm"
                  value={draft.idpType}
                  onChange={(e) => setDraft({ ...draft, idpType: e.target.value as IdpType })}
                  disabled={Boolean(draft.id)}
                >
                  {(types.length ? types : IDP_TYPE_KEYS).map((idp) => (
                    <option key={idp} value={idp}>
                      {typeLabel(idp)}
                    </option>
                  ))}
                </select>
              </label>
              <Input
                label={t('idp.displayName')}
                value={draft.displayName}
                onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
              />
            </div>
            <Input
              label={t('idp.metadataUrl')}
              value={draft.metadataUrl}
              onChange={(e) => setDraft({ ...draft, metadataUrl: e.target.value })}
              placeholder="https://login.microsoftonline.com/{tenant}/v2.0/.well-known/openid-configuration"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                label={t('idp.clientId')}
                value={draft.clientId}
                onChange={(e) => setDraft({ ...draft, clientId: e.target.value })}
              />
              <Input
                label={draft.id ? t('idp.clientSecretKeep') : t('idp.clientSecret')}
                type="password"
                value={draft.clientSecret}
                onChange={(e) => setDraft({ ...draft, clientSecret: e.target.value })}
              />
            </div>
            <label className="block text-sm">
              <span className="text-xs text-fg-muted">{t('idp.attributeMapping')}</span>
              <textarea
                className="w-full min-h-[8rem] mt-1 rounded border border-border bg-surface px-3 py-2 text-sm font-mono"
                value={draft.attributeMappingJson}
                onChange={(e) => setDraft({ ...draft, attributeMappingJson: e.target.value })}
              />
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.isActive}
                onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
              />
              <span>{t('idp.isActiveLabel')}</span>
            </label>
            <div className="flex gap-2">
              <Button onClick={save} disabled={busy || !draft.displayName}>
                {busy
                  ? t('actions.saving')
                  : draft.id
                    ? t('actions.saveChanges')
                    : t('actions.create')}
              </Button>
              <Button variant="ghost" onClick={() => setDraft(null)}>
                {t('actions.cancel')}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-4 w-4" />
            {t('idp.connectionsCount', { count: connections.length })}
          </CardTitle>
        </CardHeader>
        <CardBody>
          {connections.length === 0 ? (
            <p className="py-6 text-center text-sm text-fg-muted">{t('idp.noConnections')}</p>
          ) : (
            <ul className="divide-y divide-border">
              {connections.map((c) => (
                <li key={c.id} className="py-3 text-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{c.displayName}</p>
                        <Badge tone={c.isActive ? 'success' : 'neutral'} className="text-[10px]">
                          {c.isActive ? t('common.active') : t('common.inactive')}
                        </Badge>
                        <Badge tone="info" className="text-[10px]">
                          {typeLabel(c.idpType)}
                        </Badge>
                        {c.hasClientSecret && (
                          <Badge tone="success" className="text-[10px] inline-flex items-center gap-1">
                            <ShieldCheck className="h-3 w-3" />
                            {t('idp.secretOnFile')}
                          </Badge>
                        )}
                      </div>
                      {c.metadataUrl && (
                        <p className="text-xs text-fg-muted mt-1 truncate" title={c.metadataUrl}>
                          {c.metadataUrl}
                        </p>
                      )}
                      <p className="text-xs text-fg-muted mt-1">
                        client_id: {c.clientId ?? t('common.muted')} · {t('common.updatedAt')}{' '}
                        {new Date(c.updatedAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" onClick={() => void toggleActive(c)} disabled={busy}>
                        {c.isActive ? (
                          <PowerOff className="mr-1 h-3 w-3" />
                        ) : (
                          <Power className="mr-1 h-3 w-3" />
                        )}
                        {c.isActive ? t('actions.disable') : t('actions.enable')}
                      </Button>
                      <Button variant="ghost" onClick={() => startEdit(c)}>
                        {t('actions.edit')}
                      </Button>
                      <Button variant="ghost" onClick={() => void remove(c)} disabled={busy}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
